import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react';
import parse from 'style-to-object';
import './draggable-entity-card.css';
import {
	getCanvasEntityDOMId,
	resolveEntityColorClass,
	getCardGradientStatement,
	resolveCardSubheader,
	fromDisabledReasonsToCardState
} from '../../services/editor/ui';
import Button from '../../lib/button/Button';
import { Canvas } from '../../lib/canvas';
import InputMapProperty from './InputMapProperty';
import TestEntityButton from './TestEntityButton';
import OutputMapProperty from './OutputMapProperty';
import SuccessFailureNodes from './SuccessFailureNodes';
import {
	FunctionCallState,
	OperationState,
	EntityType,
	ConditionState,
	SearchState,
	ValueDescriptorState,
	ValueDescriptorParentChildRelation,
	ProjectState,
	createStateFromTypeAndSubscribe,
	IValueDescriptorTransfer,
	ChangeSet,
	OutputMapState,
	getDeclarationOutputedError,
	IActionOutputMapTransfer,
	OutputMapParentChildRelation,
	ChangeSetEntityChangeType,
	ActionInputMapState,
	CanvasEntityConnectionDisabledReason,
	CanvasEntityDisabledReason,
	CanvasEntityOtherDisabledReason,
	CanvasEntityTestingDisabledReason,
	ALL_CONNECTION_DISABLED_REASONS,
	addDisabledReasons,
	removeDisabledReasons,
	READABLE_ENTITY_TYPES,
	BUILT_IN_BASE_ENTITY_IDS,
	lowercaseFirstLetter,
	StateMutationAction,
	entityDisabledReasonToShortReadableError,
	safeResolveEntityName
} from '@elyx-code/project-logic-tree';
import CanvasPopup, { useCanvasPopup } from '../../services/editor/CanvasPopup';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import CanvasDropdownMenuCard from '../../lib/dropdown/CanvasDropdownMenuCard';
import ErrorMapProperty from './ErrorMapProperty';
import LogicErrorIconMenu from './logic-error/LogicErrorIconMenu';
import { useNodeDescriptionPopup } from './useNodeDescriptionPopup';
import { ICanvasElementComponentProps } from '../../services/editor/CanvasElement';
import ExpandPropertiesIconButton from './expand-properties-icon-button/ExpandPropertiesIconButton';
import NewDynamicInputButton from './new-card-input-button/NewDynamicInputButton';
import { ConnectionNodeType, EditorService } from '../../services/editor';
import NewCardInputButton from './new-card-input-button/NewCardInputButton';
import { initializeNewInputDeclarationForSearch } from '../../services/editor/utils';
import EntityIcon from '../entity-icon/EntityIcon';
import MainValueOutput from './MainValueOutput';
import EntityLockedIconMenu from './entity-locked-icon/EntityLockedIcon';
import UnreachableMarker from './UnreachableMarker';
import { popupNotification } from '../../popup-notification';

interface IPassthroughEntityCardProps extends ICanvasElementComponentProps {
	entity: OperationState | FunctionCallState | SearchState;
}

const PassthroughEntityCard = (props: IPassthroughEntityCardProps) => {
	const rootElementRef = useRef(null);

	useEffect(() => {
		const element = rootElementRef.current;

		if (!!element && document.contains(element)) {
			props.onMount?.(element);
		}

		// When the component is unmounted
		return () => {
			props.onUnmount?.();
		};
	}, []);

	useEffect(() => {
		const element = rootElementRef.current;

		props.onUpdate?.(element);
	});

	useLayoutEffect(() => {
		props.onBeforeUpdate?.();
	}, []);

	const [disabledReasons, setDisabledReasons] = useState<
		CanvasEntityDisabledReason[]
	>([]);

	const [updateKey, setValue] = useState(0);

	const useForceUpdate = () => {
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	useNodeDescriptionPopup({
		entity: props.entity,
		canvasElement: props.canvasElement,
		project: props.project
	});

	const [expandedError, setExpandedError] = useState<boolean>(false);

	const memoizedCanvasPopupBody = useCallback(
		(popup: CanvasPopup) => (
			<CanvasPopupBaseComponent
				id={
					'canvas-small-entity-card-menu-button-' +
					props.entity.id +
					'--popup--body'
				}
			>
				<CanvasDropdownMenuCard
					options={[
						{
							project: props.project,
							onClick: async () => {
								await props.project.remove(props.entity.id);
							},
							label: 'Delete',
							color: 'error',
							iconEnd: 'fa-solid fa-trash-can',
							disabled: !entity.deletable,
							tooltip: entity.deletable
								? 'Forever delete this node'
								: 'This node cannot be deleted. Because the system need to refer to it over time.',
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--delete'
						},
						{
							project: props.project,
							onClick: async () => {
								props.project?.canvas?.unselectAll();

								EditorService.navigate(
									`${props.project?.editorBasePath}/entity/${props.entity.type}/${props.entity.id}`
								);
							},
							label: 'Edit',
							iconEnd: 'fa-solid fa-pencil',
							disabled: false,
							tooltip: !props.entity.editable
								? `This ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[props.entity.type]
											.singular
									)} has been locked and can no longer be edited`
								: `Edit this ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[props.entity.type]
											.singular
									)}`,
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--edit'
						},
						{
							project: props.project,
							onClick: async () => {
								await props.project.clearEntityTestValues(
									props.entity
								);
							},
							label: 'Clear test values',
							iconEnd: 'fa-solid fa-eraser',
							disabled: !props.project.hasTestValuesForEntity(
								props.entity
							),
							tooltip: props.project.hasTestValuesForEntity(
								props.entity
							)
								? 'Clear all test values (purple overrides) for this node'
								: 'No test values to clear for this node',
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--clear-test-values'
						},
						{
							project: props.project,
							onClick: async () => {
								let actionName =
									StateMutationAction.RecalculateNode;

								if (
									props.entity.type === EntityType.Operation
								) {
									actionName =
										StateMutationAction.RecalculateOperation;
								} else if (
									props.entity.type ===
									EntityType.FunctionCall
								) {
									actionName =
										StateMutationAction.RecalculateFuncCall;
								} else if (
									props.entity.type === EntityType.Search
								) {
									actionName =
										StateMutationAction.RecalculateSearch;
								}

								const changeSet =
									props.project.logic.addChangeSet(
										new ChangeSet(
											props.project.logic,
											ProjectState.sessionAuthor,
											new Date().toISOString(),
											entity,
											false,
											actionName
										)
									);

								entity.afterAllChildrenInitialized(changeSet);

								props.project.renderAndCloseChangeSet(
									changeSet
								);
							},
							label: 'Recalculate',
							color: 'warning',
							// Bug icon
							iconEnd: 'fa-solid fa-bug',
							disabled: false,
							// Lower case the first letter of the resolved readable name
							tooltip:
								'Recalculate the state of this ' +
								lowercaseFirstLetter(
									READABLE_ENTITY_TYPES[props.entity.type]
										.shortSingular
								),
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--recalculate'
						},
						{
							project: props.project,
							onClick: async () => {
								let actionName = entity.catchesError
									? StateMutationAction.ChangeNodeToThrowErrors
									: StateMutationAction.ChangeNodeToCatchErrors;

								if (
									props.entity.type === EntityType.Operation
								) {
									actionName = entity.catchesError
										? StateMutationAction.ChangeOperationToThrowErrors
										: StateMutationAction.ChangeOperationToCatchErrors;
								} else if (
									props.entity.type ===
									EntityType.FunctionCall
								) {
									actionName = entity.catchesError
										? StateMutationAction.ChangeFuncCallToThrowErrors
										: StateMutationAction.ChangeFuncCallToCatchErrors;
								} else if (
									props.entity.type === EntityType.Search
								) {
									actionName = entity.catchesError
										? StateMutationAction.ChangeSearchToThrowErrors
										: StateMutationAction.ChangeSearchToCatchErrors;
								}

								const changeSet =
									props.project.logic.addChangeSet(
										new ChangeSet(
											props.project.logic,
											ProjectState.sessionAuthor,
											new Date().toISOString(),
											entity,
											false,
											actionName
										)
									);

								entity.metaSync(
									{
										catchesError: !entity.catchesError
									},
									changeSet
								);

								props.project.renderAndCloseChangeSet(
									changeSet
								);
							},
							label:
								entity.catchesError ||
								!!entity.errorCalls.length
									? 'Errors are caught'
									: 'Errors terminate execution',
							color: 'error',
							disabled: !!entity.errorCalls.length,
							iconEnd:
								entity.catchesError ||
								!!entity.errorCalls.length
									? 'fa-solid fa-xmark'
									: 'fa-solid fa-ban',
							subLabel: !!entity.errorCalls.length
								? 'Error call connected nodes'
								: entity.catchesError
									? 'Click to throw error instead'
									: 'Click to catch errors instead',
							tooltip: !!entity.errorCalls.length
								? 'Remove all connected nodes to the error branch if you want to throw the error instead'
								: entity.catchesError
									? 'Currently, if an error occurs, all other branches will continue executing.'
									: 'Currently, if an error occurs it will terminate all ongoing executions. Including paralel branches.',
							parentCanvasObject: popup.self,
							id:
								`canvas--${entity.type}--` +
								props.entity.id +
								'--error-handling'
						},
						{
							project: props.project,
							onClick: async () => {
								navigator.clipboard.writeText(props.entity.id);
								popupNotification({
									text: `Copied ID: ${props.entity.id}`,
									color: 'success'
								});
							},
							label: 'Copy ID',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								'Copy this node ID to clipboard for debugging',
							parentCanvasObject: popup.self,
							id: 'copy-id-' + props.entity.id
						},
						{
							project: props.project,
							onClick: async () => {
								await props.project.recalculateValidateEntity(
									props.entity
								);
								popupNotification({
									text: `Re-validated node successfully`,
									color: 'success'
								});
							},
							label: 'Re-validate',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								"Re-validate this node's state and its errors",
							parentCanvasObject: popup.self,
							id: 're-validate-' + props.entity.id
						}
					]}
				/>
			</CanvasPopupBaseComponent>
		),
		[props.entity, props.project]
	);

	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id:
				'canvas-small-entity-card-menu-button-' +
				props.entity.id +
				'--popup',
			anchorQuerySelector:
				'#canvas-small-entity-card-menu-button-' + props.entity.id,
			arrowClasses: ['dropdown-menu-card-arrow'],
			parentCanvasObject: props.canvasElement.draggableObject,
			arrowContainerSelector:
				'#canvas-small-entity-card-menu-button-' +
				props.entity.id +
				'--popup--body'
		},
		memoizedCanvasPopupBody
	);

	useEffect(() => {
		// --- Initial Setup Logic ---
		const initialNewDisabledReasons: CanvasEntityOtherDisabledReason[] = [];

		// @ts-ignore
		if (props.entity.suggestion) {
			initialNewDisabledReasons.push(
				CanvasEntityOtherDisabledReason.Suggestion
			);
		}

		if (props.project.canvas?.disabled) {
			initialNewDisabledReasons.push(
				CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
			);
		}

		if (!!initialNewDisabledReasons.length) {
			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(
					currentReasons,
					initialNewDisabledReasons
				);
				return config.reasons;
			});
		} else {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		}

		const handleCanvasGloballyEnabled = () => {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		};
		const handleCanvasGloballyDisabled = () => {
			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		};

		const handleEntityUpdate = (payload: any) => {
			let hasUpdated = false;
			const newDisabledReasons: CanvasEntityTestingDisabledReason[] = [];
			const disabledReasonsToRemove: CanvasEntityTestingDisabledReason[] =
				[];

			props.project.test?.disabledConnectionsNotInTest(
				props.canvasElement.draggableObject,
				props.entity
			);

			if (
				!!props.project.test &&
				!props.project.test?.hasEntity(props.entity) &&
				!props.project.test?.getCanBeAddedToTest(props.entity)
			) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);

				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
			}

			if (
				(props.project.test?.hasEntity(props.entity) ||
					props.project.test?.getCanBeAddedToTest(props.entity)) &&
				props.canvasElement.draggableObject.disabled
			) {
				props.project.canvas?.enable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);

				disabledReasonsToRemove.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
			}

			if (
				!!props.project.test &&
				!props.project.test?.hasEntity(props.entity)
			) {
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
			} else if (
				!!props.project.test &&
				props.project.test?.hasEntity(props.entity)
			) {
				disabledReasonsToRemove.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
			}

			if (newDisabledReasons.length || disabledReasonsToRemove.length) {
				setDisabledReasons((currentReasons) => {
					const config = addDisabledReasons(
						currentReasons,
						newDisabledReasons
					);
					const finalConfig = removeDisabledReasons(
						config.reasons,
						disabledReasonsToRemove
					);
					return finalConfig.reasons;
				});
				hasUpdated = true;
			}

			if (!hasUpdated) {
				forceUpdate();
			}
		};

		const handleTestSetupStarted = () => {
			const newDisabledReasons: CanvasEntityTestingDisabledReason[] = [];

			if (
				!!props.project.test &&
				!props.project.test?.hasEntity(props.entity) &&
				!props.project.test?.getCanBeAddedToTest(props.entity)
			) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				props.project.test?.disabledConnectionsNotInTest(
					props.canvasElement.draggableObject,
					props.entity
				);

				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
			}

			if (!props.project.test?.hasEntity(props.entity)) {
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
			}

			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(
					currentReasons,
					newDisabledReasons
				);
				return config.reasons;
			});
		};

		const handleExecutionStart = () => {
			if (!!props.project.test?.hasEntity(props.entity)) {
				forceUpdate();
			}
		};

		const handleTestExited = () => {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest,
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				]);
				return config.reasons;
			});

			if (props.canvasElement.draggableObject.disabled) {
				props.project.canvas?.enable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
			}

			props.canvasElement.draggableObject.connections.forEach(
				(connection) => {
					connection.enable();
					connection.element.classList.remove('disabled');

					if (connection.complementaryLineElement) {
						connection.complementaryLineElement.classList.remove(
							'disabled'
						);
					}
				}
			);
		};

		const handleCanvasConnectionStart = (payload: any) => {
			if (!payload.compatible?.[props.entity.id]) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);

				const newOwnConnectionDisabledReasons: CanvasEntityConnectionDisabledReason[] =
					payload.incompatible?.[props.entity.id]?.reasons || [];

				setDisabledReasons((currentReasons) => {
					const config = addDisabledReasons(
						currentReasons,
						newOwnConnectionDisabledReasons
					);
					return config.reasons;
				});
			} else {
				forceUpdate();
			}
		};

		const handleCanvasConnectionEnd = () => {
			props.project.canvas?.enable(props.canvasElement.draggableObject, {
				quiet: true
			});

			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(
					currentReasons,
					ALL_CONNECTION_DISABLED_REASONS
				);
				return config.reasons;
			});
		};

		props.project.on(
			'canvas-globally-enabled',
			handleCanvasGloballyEnabled
		);
		props.project.on(
			'canvas-globally-disabled',
			handleCanvasGloballyDisabled
		);
		props.project.on(props.entity.id, handleEntityUpdate);
		props.project.on('test-setup-started', handleTestSetupStarted);
		props.project.on('execution-start', handleExecutionStart);
		props.project.on('test-exited', handleTestExited);
		props.project.on(
			'canvas-connection-start',
			handleCanvasConnectionStart
		);
		props.project.on('canvas-connection-end', handleCanvasConnectionEnd);

		return () => {
			props.project.off(
				'canvas-globally-enabled',
				handleCanvasGloballyEnabled
			);
			props.project.off(
				'canvas-globally-disabled',
				handleCanvasGloballyDisabled
			);
			props.project.off(props.entity.id, handleEntityUpdate);
			props.project.off('test-setup-started', handleTestSetupStarted);
			props.project.off('execution-start', handleExecutionStart);
			props.project.off('test-exited', handleTestExited);
			props.project.off(
				'canvas-connection-start',
				handleCanvasConnectionStart
			);
			props.project.off(
				'canvas-connection-end',
				handleCanvasConnectionEnd
			);
		};
	}, [
		props.project,
		props.entity,
		props.canvasElement,
		updateKey,
		// @ts-ignore
		props.entity.suggestion
	]);

	useEffect(() => {
		props.canvasElement.draggableObject.updateConnections();
	});

	const entity = props.entity;

	const classes = ['canvas-draggable-card'];

	const dynamicInputsValueDescriptors: ValueDescriptorState[] = [];

	if (props.entity.type !== EntityType.Search) {
		props.entity.declaration.inputs.forEach((input) => {
			if (
				(input as ValueDescriptorState).parentRelationType ===
				ValueDescriptorParentChildRelation.DynamicArgument
			) {
				dynamicInputsValueDescriptors.push(
					input as ValueDescriptorState
				);
			}
		});
	}

	const commonInputButtonAllowed =
		!props.project.test &&
		!disabledReasons.includes(CanvasEntityOtherDisabledReason.Suggestion) &&
		!disabledReasons.includes(
			CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
		) &&
		!disabledReasons.includes(
			CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
		) &&
		!disabledReasons.includes(
			CanvasEntityTestingDisabledReason.CannotBeAddedToTest
		) &&
		(!props.project.onGoingConnectionNodeType ||
			(props.project.onGoingConnectionNodeType &&
				props.project.onGoingConnectionNodeType ===
					ConnectionNodeType.PropertyOut)) &&
		!disabledReasons.includes(
			CanvasEntityConnectionDisabledReason.NotInScope
		);

	const showNewDynamicInputButton =
		commonInputButtonAllowed &&
		// If the entity is a function call or a search, we don't show the new input button
		![EntityType.FunctionCall, EntityType.Search].includes(entity.type);

	const showNewInlineDeclaredInputButton =
		commonInputButtonAllowed &&
		entity.type === EntityType.Search &&
		!showNewDynamicInputButton;

	if (entity.deleted) {
		return null;
	}

	return (
		<div
			ref={rootElementRef}
			className={classes.join(' ')}
			id={getCanvasEntityDOMId(props.entity)}
			{...{
				[Canvas.OBJ_ATTR_NAME]: getCanvasEntityDOMId(props.entity)
			}}
			onClick={(e) => {
				e.stopPropagation();
			}}
		>
			<TestEntityButton
				entity={props.entity}
				canvasElement={props.canvasElement}
				project={props.project}
				parentDisabledReasons={disabledReasons}
			/>
			{/* Floats "Unreachable!" above the right side of the header when
				the entity carries the blocked-reachability error. Same DOM
				placement as VariableScopeMarker on VariableCard so the
				`position: absolute` offsets resolve against the outer
				canvas-draggable-card div. Returns null when reachable. */}
			<UnreachableMarker
				entity={props.entity}
				parentDisabledReasons={disabledReasons}
				project={props.project}
				draggableObject={props.canvasElement.draggableObject}
			/>
			<div
				className={[
					'canvas-draggable-card__body',
					...(!!disabledReasons.length
						? ['canvas-draggable-card__body__disabled']
						: []),
					resolveEntityColorClass(props.entity.type, {
						state: fromDisabledReasonsToCardState(disabledReasons)
					})
				].join(' ')}
			>
				<header
					className="canvas-draggable-card__header"
					{...{
						[Canvas.OBJ_ATTR_NAME]:
							getCanvasEntityDOMId(props.entity) + '--header'
					}}
					style={parse(
						getCardGradientStatement(entity, {
							state: fromDisabledReasonsToCardState(
								disabledReasons
							)
						})
					)}
					onMouseOver={(e: any) => {
						if (!!props.project.canvas?.activeConnectionDrawing) {
							if (
								!!props.project
									.onGoingConnectionIncompatibleEntities?.[
									props.entity.id
								]
							) {
								// Read the disabled reasons and add a tooltip to the mouse
								const reasonsMessage =
									props.project.onGoingConnectionIncompatibleEntities?.[
										props.entity.id
									].reasons
										.map((reason) =>
											entityDisabledReasonToShortReadableError(
												reason
											)
										)
										.join('.\n');

								props.project.canvas?.showTooltip(
									'connection-disabled-reasons-test-entity-button',
									reasonsMessage
								);
								if (props.project.canvas) {
									props.project.canvas.tooltip.style.fontSize =
										'28px';
									props.project.canvas.canvasElement.style.cursor =
										'not-allowed';
								}
							}
						}
					}}
					onMouseLeave={(e: any) => {
						props.project.canvas?.hideTooltip(
							'connection-disabled-reasons-test-entity-button'
						);
					}}
				>
					<div className="canvas-draggable-card__header__left-block">
						<EntityIcon
							entity={entity}
							size="medium"
							color="black"
							parentDisabledReasons={disabledReasons}
							project={props.project}
							parentCanvasObject={
								props.canvasElement.draggableObject
							}
						/>
						<span className="canvas-draggable-card__header__labels">
							<h3>
								{safeResolveEntityName(
									entity,
									props.project.logic
								)}
							</h3>
							<p>{resolveCardSubheader(entity)}</p>
						</span>
					</div>
					<div className="canvas-draggable-card__header__actions">
						<EntityLockedIconMenu
							entity={entity}
							project={props.project}
							canvasElement={props.canvasElement}
							parentDisabledReasons={disabledReasons}
						/>
						<LogicErrorIconMenu
							entity={entity}
							project={props.project}
							canvasElement={props.canvasElement}
							parentDisabledReasons={disabledReasons}
						/>
						<Button
							variant="text"
							color="grey"
							iconEnd="fa-solid fa-ellipsis-v"
							size="small"
							ref={canvasPopup.anchorRef}
							className="canvas-small-entity-card-menu-button"
							id={`canvas-small-entity-card-menu-button-${entity.id}`}
							disabled={!!disabledReasons.length}
							onClick={(e: any) => {
								e.stopPropagation();
								canvasPopup?.toggle();
							}}
							onDoubleClick={(
								e: React.MouseEvent<HTMLButtonElement>
							) => {
								e.stopPropagation();
								if (disabledReasons.length) return;
								props.project?.canvas?.unselectAll();
								EditorService.navigate(
									`${props.project?.editorBasePath}/entity/${props.entity.type}/${props.entity.id}`
								);
							}}
						/>
						<SuccessFailureNodes
							entity={entity}
							draggableObject={
								props.canvasElement.draggableObject
							}
							project={props.project}
							parentDisabledReasons={disabledReasons}
						/>
					</div>
				</header>
				<div className="canvas-draggable-card__content">
					{/* {((entity as OperationState)?.process?.writesValues || []).length >
						0 && (
						<ExecutionInProgressValue
							entity={entity as OperationState}
							draggableObject={props.canvasElement.draggableObject}
							project={props.project}
							parentDisabledReasons={disabledReasons}
						/>
					)} */}
					{!!(entity as FunctionCallState).error ? (
						<ErrorMapProperty
							outputMap={(entity as FunctionCallState).error}
							entity={entity}
							draggableObject={
								props.canvasElement.draggableObject
							}
							project={props.project}
							parentDisabledReasons={disabledReasons}
						/>
					) : (
						<>
							{expandedError && (
								<ErrorMapProperty
									outputMap={
										(entity as OperationState).error || null
									}
									entity={entity}
									draggableObject={
										props.canvasElement.draggableObject
									}
									project={props.project}
									parentDisabledReasons={disabledReasons}
								/>
							)}

							<ExpandPropertiesIconButton
								project={props.project}
								expanded={expandedError}
								draggableObject={
									props.canvasElement.draggableObject
								}
								onClick={() => {
									const errorDeclaration =
										getDeclarationOutputedError(entity);

									// Create error output-map entity before expanding the error for the first time
									if (!entity.error && !expandedError) {
										let actionName =
											StateMutationAction.AddErrorOutputToNode;

										if (
											props.entity.type ===
											EntityType.Operation
										) {
											actionName =
												StateMutationAction.AddErrorOutputToOperation;
										} else if (
											props.entity.type ===
											EntityType.FunctionCall
										) {
											actionName =
												StateMutationAction.AddErrorOutputToFuncCall;
										} else if (
											props.entity.type ===
											EntityType.Search
										) {
											actionName =
												StateMutationAction.AddErrorOutputToSearch;
										}

										const changeSet =
											props.project.logic.addChangeSet(
												new ChangeSet(
													props.project.logic,
													ProjectState.sessionAuthor,
													new Date().toISOString(),
													entity,
													false,
													actionName
												)
											);

										const newOutputMapTransfer: IActionOutputMapTransfer =
											OutputMapState.new(
												changeSet
											) as IActionOutputMapTransfer;
										newOutputMapTransfer.parent =
											entity.toReference();
										newOutputMapTransfer.declaration =
											(errorDeclaration?.toJSON() as IValueDescriptorTransfer) ||
											null;
										newOutputMapTransfer.parentRelationType =
											OutputMapParentChildRelation.Error;

										const newInstance =
											createStateFromTypeAndSubscribe(
												newOutputMapTransfer,
												props.project.logic
											) as OutputMapState;

										newInstance.hydrateAncestors();
										newInstance.addSelfToProject(changeSet);

										changeSet.add(
											newInstance,
											ChangeSetEntityChangeType.Added
										);

										newInstance.initChildren(changeSet);

										props.project.renderAndCloseChangeSet(
											changeSet
										);
									}

									setExpandedError(!expandedError);
								}}
								id={`expand-inputs-icon-button--${entity.id}`}
								tooltip={
									expandedError
										? 'Click to hide unused error output'
										: 'Click to show unused error output'
								}
								tooltipPlacement="top"
								style={{ marginTop: '6px' }}
								parentDisabledReasons={disabledReasons}
							/>
						</>
					)}

					{entity.getDataType(null)?.entity?.id !==
						BUILT_IN_BASE_ENTITY_IDS['primitive-entity'].void
							.id && (
						<MainValueOutput
							entity={entity}
							canvasElement={props.canvasElement}
							project={props.project}
							parentDisabledReasons={disabledReasons}
						/>
					)}

					{entity.inputs.length > 0 && (
						<ul className="canvas-draggable-card__property-list">
							{entity.inputs
								.sort((a, b) => {
									// Sort by `.index` property
									if (a.index < b.index) {
										return -1;
									}
									if (a.index > b.index) {
										return 1;
									}
									return 0;
								})
								.map((inputMap) => {
									return (
										<InputMapProperty
											key={inputMap.id}
											inputMap={inputMap}
											entity={props.entity}
											canvasElement={props.canvasElement}
											project={props.project}
											parentDisabledReasons={
												disabledReasons
											}
										/>
									);
								})}
						</ul>
					)}
					{showNewInlineDeclaredInputButton && (
						<div
							className={[
								'canvas-draggable-card__action-alt',
								'align-left'
							].join(' ')}
						>
							<NewCardInputButton
								iconStart="fa-solid fa-plus"
								size="small"
								parentDisabledReasons={disabledReasons}
								entity={entity as SearchState}
								project={props.project}
								canvasElement={props.canvasElement}
								onClick={() => {
									const { declaration: newInputDeclaration } =
										initializeNewInputDeclarationForSearch(
											props.project,
											entity,
											'Input'
										);

									EditorService.navigate(
										`${props.project?.editorBasePath}/entity/${EntityType.ValueDescriptor}/${newInputDeclaration.id}`
									);
								}}
							>
								Input
							</NewCardInputButton>
						</div>
					)}
					{showNewDynamicInputButton &&
						dynamicInputsValueDescriptors
							.sort((a, b) => {
								// Sort by `.index` property
								if (a.index < b.index) {
									return -1;
								}
								if (a.index > b.index) {
									return 1;
								}
								return 0;
							})
							.map((valueDescriptor) => {
								return (
									<div
										key={valueDescriptor.id}
										className={[
											'canvas-draggable-card__action-alt',
											'align-left'
										].join(' ')}
									>
										<NewDynamicInputButton
											iconStart="fa-solid fa-plus"
											size="small"
											parentDisabledReasons={
												disabledReasons
											}
											entity={
												entity as
													| OperationState
													| ConditionState
											}
											inputValueDescriptor={
												valueDescriptor
											}
											project={props.project}
											canvasElement={props.canvasElement}
											onClick={() => {
												let actionName =
													StateMutationAction.AddInputToNode;

												if (
													props.entity.type ===
													EntityType.Operation
												) {
													actionName =
														StateMutationAction.AddInputToOperation;
												} else if (
													props.entity.type ===
													EntityType.FunctionCall
												) {
													actionName =
														StateMutationAction.AddInputToFuncCall;
												} else if (
													props.entity.type ===
													EntityType.Search
												) {
													actionName =
														StateMutationAction.AddInputToSearch;
												}

												const changeSet =
													props.project.logic.addChangeSet(
														new ChangeSet(
															props.project.logic,
															ProjectState.sessionAuthor,
															new Date().toISOString(),
															entity,
															false,
															actionName
														)
													);

												const newInputMapTransfer =
													ActionInputMapState.new(
														changeSet
													);
												newInputMapTransfer.parent =
													entity.toReference();
												newInputMapTransfer.declaration =
													valueDescriptor.toJSON() as IValueDescriptorTransfer;
												newInputMapTransfer.index =
													entity.inputs.length;

												const newInputMap =
													createStateFromTypeAndSubscribe(
														newInputMapTransfer,
														props.project.logic
													) as ValueDescriptorState;

												newInputMap.hydrateAncestors();

												changeSet.add(
													newInputMap,
													ChangeSetEntityChangeType.Added
												);

												newInputMap.addSelfToProject(
													changeSet
												);
												newInputMap.afterAllChildrenInitialized(
													changeSet
												);

												entity.afterAllChildrenInitialized(
													changeSet
												);

												EditorService.navigate(
													`${props.project?.editorBasePath}/entity/${EntityType.InputMap}/${newInputMap.id}`
												);

												props.project.renderAndCloseChangeSet(
													changeSet
												);
											}}
										>
											{safeResolveEntityName(
												valueDescriptor,
												props.project.logic
											)}
										</NewDynamicInputButton>
									</div>
								);
							})}
					{((entity as FunctionCallState).outputs || []).length >
						0 && (
						<ul className="canvas-draggable-card__property-list">
							{((entity as FunctionCallState).outputs || [])
								.sort((a, b) => {
									// Sort by `.index` property
									if (a.index < b.index) {
										return -1;
									}
									if (a.index > b.index) {
										return 1;
									}
									return 0;
								})
								.map((outputMap) => (
									<OutputMapProperty
										key={outputMap.id}
										outputMap={outputMap}
										entity={props.entity}
										canvasElement={props.canvasElement}
										project={props.project}
										parentDisabledReasons={disabledReasons}
										size="small"
									/>
								))}
						</ul>
					)}
				</div>
			</div>
		</div>
	);
};

export default PassthroughEntityCard;
